import { useEffect, useState } from 'react';
import { Modal, Button, Stack, Cluster, Text, Field, Select, Banner } from './ui';
import { getStripe } from '../lib/stripe';
import {
  fetchMyAddresses, fetchPostageQuoteResult, buyNowCheckout, fetchMyCards,
  type ShippingAddress, type PostageQuoteResult, type MemberCard,
} from '../lib/queries';

/**
 * Buy Now checkout. Confirms the purchase, lets the buyer pick a delivery
 * address (re-quoting live shipping each time), then charges on-session and
 * completes any bank 3DS/SCA inline. On success the lot is theirs.
 */

function dollars(cents: number): string {
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function addrLine(a: ShippingAddress): string {
  return [a.label, a.city, a.country].filter(Boolean).join(' · ');
}

function cardLine(c: MemberCard): string {
  const brand = c.card_brand ? c.card_brand.charAt(0).toUpperCase() + c.card_brand.slice(1) : 'Card';
  const exp = c.card_exp_month ? ` (exp ${String(c.card_exp_month).padStart(2, '0')}/${String(c.card_exp_year ?? '').slice(-2)})` : '';
  return `${brand} ···· ${c.card_last4 ?? '••••'}${exp}`;
}

export function BuyNowCheckoutModal({
  postId, lotName, buyNowPriceCents, initialAddressId, onClose, onPurchased,
}: {
  postId: string;
  lotName: string;
  buyNowPriceCents: number;
  initialAddressId?: string | null;
  onClose: () => void;
  onPurchased: () => void;
}) {
  const [addresses, setAddresses] = useState<ShippingAddress[]>([]);
  const [addrLoaded, setAddrLoaded] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(initialAddressId ?? null);
  const [cards, setCards] = useState<MemberCard[]>([]);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [quote, setQuote] = useState<PostageQuoteResult | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Load the address book, pick the default (or the lot's chosen address).
  useEffect(() => {
    let cancelled = false;
    fetchMyAddresses()
      .then((list) => {
        if (cancelled) return;
        setAddresses(list);
        setSelectedId((cur) => cur ?? list.find((a) => a.is_default)?.id ?? list[0]?.id ?? null);
      })
      .catch(() => { /* leave empty -> prompt to add */ })
      .finally(() => { if (!cancelled) setAddrLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  // Load saved cards; preselect the default. Only shown when there's a choice.
  useEffect(() => {
    let cancelled = false;
    fetchMyCards()
      .then((list) => {
        if (cancelled) return;
        setCards(list);
        setSelectedCardId((cur) => cur ?? list.find((c) => c.is_default)?.id ?? list[0]?.id ?? null);
      })
      .catch(() => { /* fall back to default card server-side */ });
    return () => { cancelled = true; };
  }, []);

  // (Re)quote live shipping whenever the chosen address changes.
  useEffect(() => {
    if (!selectedId) { setQuote(null); return; }
    let cancelled = false;
    setQuoting(true);
    fetchPostageQuoteResult(postId, selectedId)
      .then((q) => { if (!cancelled) setQuote(q); })
      .finally(() => { if (!cancelled) setQuoting(false); });
    return () => { cancelled = true; };
  }, [postId, selectedId]);

  const postageCents = quote?.available ? quote.amount_cents : null;
  const totalCents = postageCents != null ? buyNowPriceCents + postageCents : null;
  const canConfirm = !!selectedId && quote?.available === true && !quoting && !busy;

  async function confirm() {
    if (!canConfirm || !selectedId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await buyNowCheckout(postId, selectedId, selectedCardId ?? undefined);

      if (res.status === 'requires_action' && res.client_secret) {
        // The bank wants 3DS - run it inline against the existing PaymentIntent.
        const stripe = await getStripe();
        if (!stripe) throw new Error('Card payments are unavailable right now.');
        const { error: actionErr } = await stripe.handleNextAction({ clientSecret: res.client_secret });
        if (actionErr) {
          setError(actionErr.message ?? 'We could not confirm the payment. Your lot is held - try again from "Complete payment".');
          setBusy(false);
          return;
        }
        // Confirmed; the webhook settles the ledger.
        setDone(true);
        return;
      }

      if (res.status === 'succeeded' || res.status === 'processing') {
        setDone(true);
        return;
      }

      // failed / pending: the lot is reserved to them but the charge did not go
      // through. Point them at the same recovery path as a stuck win.
      setError(res.error ?? 'Your card was declined. The lot is held for you - update your card and complete the payment.');
      setBusy(false);
    } catch (err: any) {
      setError(err?.message ?? 'Checkout failed. Try again.');
      setBusy(false);
    }
  }

  // Success view.
  if (done) {
    return (
      <Modal open onClose={() => { onPurchased(); onClose(); }} title="Lot secured" size="sm">
        <Stack gap="group">
          <Text role="body">
            You bought <strong>{lotName}</strong>. We have charged your card and
            your order is now with the team to pack and post.
          </Text>
          <div className="flex justify-end">
            <Button type="button" variant="primary" size="md" onClick={() => { onPurchased(); onClose(); }}>
              Done
            </Button>
          </div>
        </Stack>
      </Modal>
    );
  }

  return (
    <Modal open onClose={busy ? () => {} : onClose} title="Checkout" size="sm">
      <Stack gap="group">
        <Stack gap="tight">
          <Text role="eyebrow">Buy now</Text>
          <Text role="body"><strong>{lotName}</strong></Text>
        </Stack>

        {addrLoaded && addresses.length === 0 ? (
          <Banner tone="warning" title="Add a delivery address">
            You need a delivery address before you can check out. Add one in your
            profile under Addresses, then come back.
          </Banner>
        ) : (
          <Field label="Deliver to" htmlFor="checkout-address">
            <Select
              id="checkout-address"
              value={selectedId ?? ''}
              onChange={(e) => setSelectedId(e.target.value || null)}
              disabled={busy || !addrLoaded}
            >
              {addresses.map((a) => (
                <option key={a.id} value={a.id}>
                  {addrLine(a)}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {cards.length > 1 && (
          <Field label="Pay with" htmlFor="checkout-card">
            <Select
              id="checkout-card"
              value={selectedCardId ?? ''}
              onChange={(e) => setSelectedCardId(e.target.value || null)}
              disabled={busy}
            >
              {cards.map((c) => (
                <option key={c.id} value={c.id}>
                  {cardLine(c)}{c.is_default ? ' · default' : ''}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {/* Price breakdown */}
        <Stack gap="tight" className="border-t border-border pt-3">
          <Cluster justify="between">
            <Text role="small" muted>Item</Text>
            <Text role="small">{dollars(buyNowPriceCents)}</Text>
          </Cluster>
          <Cluster justify="between">
            <Text role="small" muted>Shipping</Text>
            <Text role="small">
              {quoting ? 'Calculating…'
                : postageCents != null ? dollars(postageCents)
                : quote && !quote.available ? 'Unavailable'
                : '—'}
            </Text>
          </Cluster>
          <Cluster justify="between" className="border-t border-border pt-2">
            <Text role="label" as="span">Total</Text>
            <Text role="label" as="span">{totalCents != null ? dollars(totalCents) : '—'}</Text>
          </Cluster>
        </Stack>

        {quote && !quote.available && (
          <Text role="small" className="text-danger">
            {quote.reason === 'no_address'
              ? 'Pick a delivery address to see shipping.'
              : 'We could not calculate shipping for this address. Try another address or try again shortly.'}
          </Text>
        )}

        <Text role="caption" faint>
          International orders may have import duties or taxes due on delivery,
          set by your country. Your card is charged the total above now.
        </Text>

        {error && <Text role="small" className="text-danger" aria-live="polite">{error}</Text>}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" size="md" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="button" variant="accent" size="md" onClick={confirm} loading={busy} disabled={!canConfirm}>
            {totalCents != null ? `Pay ${dollars(totalCents)}` : 'Pay'}
          </Button>
        </div>
      </Stack>
    </Modal>
  );
}
